import { SvgConsumer } from '@components/icons/svgConsumer';
import { Button } from '@components/ui/button';
import { useRemoveExternalSchedule } from '@services/schedules/external/remove';
import { useCompleteTask } from '@services/tasks/complete';
import { useRevertTask } from '@services/tasks/revert';
import { useTranslation } from 'react-i18next';
import { ExternalScheduleActionsProps } from './types';
import { cn } from '@utils/cn';
import { RemoveScheduleDialog } from '../../remove';
import { useState } from 'react';
import { AppTooltip } from '@components/ui/tooltip/tooltip';

export const ExternalScheduleActions = ({
  setOpen,
  locked,
  completed,
  jobId,
  taskId: id,
  scheduleId,
  concreteDirectId,
  finalYards,
  readOnly,
  scheduleType,
  unsavedChanges,
}: ExternalScheduleActionsProps) => {
  const { t } = useTranslation('app');
  const [remove, setRemove] = useState(false);

  const complete = useCompleteTask();
  const revert = useRevertTask();

  const removeSchedule = useRemoveExternalSchedule({
    id: scheduleId || '',
    jobId: jobId || '',
    taskId: id,
    callback: (resp) => {
      if (!resp) return;
      const { success } = resp;
      if (!success) return;
      setOpen(false);
    },
  });

  const handleAction = () => {
    if (!id || !jobId) return;
    if (completed) revert.mutate({ id, jobId });
    else complete.mutate({ id, jobId });
    setOpen(false);
  };

  const disabled = complete.isLoading || revert.isLoading || removeSchedule.isLoading || locked;
  const newSchedule = !scheduleId || scheduleId === 'new';

  const markAsDoneButton = (
    <Button
      variant={completed ? 'successSecondary' : 'secondary'}
      disabled={
        (!scheduleId && !completed) || disabled || Boolean(concreteDirectId) || unsavedChanges
      }
      className={cn(unsavedChanges && 'pointer-events-none')}
      onClick={handleAction}>
      <SvgConsumer id="markAsDone" className="text-xl" />
      {completed ? t('actions.done') : t('actions.markAsDone')}
    </Button>
  );

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        !id || readOnly ? 'justify-end' : 'justify-between',
      )}>
      {!readOnly &&
        id &&
        (unsavedChanges ? (
          <AppTooltip side="top" context={t('schedules.unsavedMaterialChanges')}>
            <span className="cursor-not-allowed">{markAsDoneButton}</span>
          </AppTooltip>
        ) : (
          markAsDoneButton
        ))}
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={() => setOpen(false)}>
          {t('actions.cancel')}
        </Button>
        {!readOnly && !newSchedule && scheduleType !== 'dependent' && (
          <Button
            disabled={newSchedule || completed || disabled}
            variant="dangerSecondary"
            onClick={() => setRemove(true)}>
            {t('actions.removeSchedule')}
          </Button>
        )}
        {!readOnly && (
          <Button
            variant="default"
            type="submit"
            disabled={completed || disabled || (newSchedule && Boolean(concreteDirectId))}>
            {t(`actions.${finalYards ? 'update' : 'save'}`)}
          </Button>
        )}
      </div>
      <RemoveScheduleDialog
        open={remove}
        setOpen={setRemove}
        onConfirm={removeSchedule.mutate}
        disabled={removeSchedule.isLoading}
      />
    </div>
  );
};
